import React, {
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import qs from 'qs';
import cx from 'classnames';
import {
  isLayoutMock,
  routes,
  URL,
} from 'appConstants';
import {
  ButtonV4,
  Link,
  NoData,
  Preloader,
} from 'components';
import { useRequest } from 'hooks';
import {
  _has_t_back,
  _t,
  _t_back,
  dateToUiFormat,
  getEventsFromDate,
  getEventCardTag,
  hasEventDetails,
  isEventFromCalendarPeriod,
  isEventRegistrationOpen,
  matchEventFilters,
  openApplicationForm,
  sortEventsByDate,
  validateEmptyParams,
} from 'utils';
import type { EventFilterId } from 'utils';
import type { Event, GetResponse } from 'types';
import { RequestStatus } from 'types';
import { getSeeAlso } from 'containers/Resolutions/shared';

import banner from 'assets/img/main-v4/hero-curve.png';
import iconSearch from 'assets/img/header-v4/icon-search.svg';
import iconFilter from 'assets/img/main-v4/icon-filter.svg';
import iconPlus from 'assets/img/main-v4/icon-plus.svg';
import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';
import eventHero from 'assets/img/events-v4/event-hero.png';

import styles from './styles.module.scss';

const PAGE_SIZE_FIRST = 7;
const PAGE_SIZE_REST = 8;

const getPageSize = (pageIndex: number) => (
  pageIndex === 0 ? PAGE_SIZE_FIRST : PAGE_SIZE_REST
);

const getPageOffset = (pageIndex: number) => {
  if (pageIndex <= 0) {
    return 0;
  }
  return PAGE_SIZE_FIRST + (pageIndex - 1) * PAGE_SIZE_REST;
};

const getTotalPages = (total: number) => {
  if (total <= PAGE_SIZE_FIRST) {
    return 1;
  }
  return 1 + Math.ceil((total - PAGE_SIZE_FIRST) / PAGE_SIZE_REST);
};

type FilterId = EventFilterId;

const FILTERS: { id: FilterId, label: string }[] = [
  { id: 'exhibitions', label: 'Выставки' },
  { id: 'forums', label: 'Форумы' },
  { id: 'lessons', label: 'Лекции' },
  { id: 'tours', label: 'Экскурсии' },
  { id: 'missions', label: 'Бизнес-миссии' },
];

const FALLBACK_TITLE = 'Форум будущих технологий “Биоэкономика”';
const FALLBACK_META = '26.02 - 27.02\nМосква, Конгресс-Центр Международной Торговли';

const FALLBACK = Array.from({ length: PAGE_SIZE_FIRST }, (_, index) => ({
  id: index + 1,
  slug: index === 0 ? 'demo-event' : `demo-event-${index + 1}`,
  tag: 'форум',
  title: FALLBACK_TITLE,
  meta: FALLBACK_META,
  featured: index === 0,
  canLink: false,
}));

const formatMeta = (item: Event) => {
  const start = dateToUiFormat(item.date_of_event_start || '').slice(0, 5);
  const end = dateToUiFormat(item.date_of_event_end || '').slice(0, 5);
  const dates = [start, end].filter(Boolean).join(' - ');
  const location = _t_back(item, 'location') || '';
  return [dates, location].filter(Boolean).join('\n');
};

const seeAlsoItems = getSeeAlso(['events']);

type Props = {
  targetDetailsPath?: string,
  embedded?: boolean,
};

const EventsList = ({
  targetDetailsPath = routes.events.root,
  embedded = false,
}: Props) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [activeFilters, setActiveFilters] = useState<FilterId[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const layoutMock = isLayoutMock();
  const { data, status, request } = useRequest<GetResponse<Event[]>>({
    errback: () => undefined,
  });

  useEffect(() => {
    if (layoutMock) {
      return;
    }
    request({
      method: 'get',
      url: URL.EVENTS.GET,
      params: validateEmptyParams({
        params: {
          search: query,
          start_filter_date: getEventsFromDate(),
          limit: 100,
          offset: 0,
        },
        emptyParamsToDelete: ['search'],
      }),
      paramsSerializer: (value) => qs.stringify(value, { indices: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutMock]);

  const showFallback = layoutMock || status === RequestStatus.ERROR || (
    status === RequestStatus.SUCCESS && !(data?.results || []).length
  );

  const filteredItems = useMemo(() => {
    if (showFallback) {
      return [];
    }
    return (data?.results || [])
      .filter((item) => _has_t_back(item))
      .filter((item) => isEventFromCalendarPeriod(item))
      .filter((item) => matchEventFilters(item, activeFilters));
  }, [data, showFallback, activeFilters]);

  const totalPages = showFallback
    ? 1
    : Math.max(1, getTotalPages(filteredItems.length));

  const safePage = Math.min(page, totalPages - 1);
  const pageSize = getPageSize(safePage);
  const pageOffset = getPageOffset(safePage);

  const cards = useMemo(() => {
    if (showFallback) {
      return FALLBACK.map((item) => ({
        ...item,
        title: _t(item.title),
        meta: _t(item.meta),
        tag: _t(item.tag),
        canRegister: true,
      }));
    }

    return sortEventsByDate(filteredItems)
      .slice(pageOffset, pageOffset + pageSize)
      .map((item, index) => ({
        id: item.id,
        slug: item.slug,
        tag: _t(getEventCardTag(item)),
        title: _t_back(item, 'title'),
        meta: formatMeta(item),
        featured: safePage === 0 && index === 0,
        canLink: hasEventDetails(item),
        canRegister: isEventRegistrationOpen(item),
      }));
  }, [filteredItems, showFallback, safePage, pageOffset, pageSize]);

  const toggleFilter = (id: FilterId) => {
    setPage(0);
    setActiveFilters((prev) => (
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    ));
  };

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (layoutMock) {
      return;
    }
    setPage(0);
    request({
      method: 'get',
      url: URL.EVENTS.GET,
      params: validateEmptyParams({
        params: {
          search: query,
          start_filter_date: getEventsFromDate(),
          limit: 100,
          offset: 0,
        },
        emptyParamsToDelete: ['search'],
      }),
      paramsSerializer: (value) => qs.stringify(value, { indices: false }),
    });
  };

  return (
    <div className={cx(styles.page, embedded && styles.pageEmbedded)}>
      {!embedded ? (
        <div className={styles.banner} aria-hidden>
          <img src={banner} alt="" />
        </div>
      ) : null}

      {!embedded ? (
        <section className={styles.head}>
          <h1 className={styles.title}>{_t('Календарь событий')}</h1>
          <nav className={styles.crumbs} aria-label={_t('Навигация')}>
            <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
            <span className={styles.crumbLine} aria-hidden />
            <span className={styles.crumbCurrent}>{_t('Мероприятия')}</span>
          </nav>
        </section>
      ) : null}

      <div className={styles.toolbar}>
        <form className={styles.search} onSubmit={onSearchSubmit}>
          <input
            className={styles.searchInput}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={_t('Введите наименование или дату мероприятия')}
            aria-label={_t('Поиск мероприятий')}
          />
          <button type="submit" className={styles.searchBtn} aria-label={_t('Искать')}>
            <img src={iconSearch} alt="" width={14} height={14} />
          </button>
        </form>

        <div className={styles.filters}>
          <div
            id="events-filters"
            className={cx(styles.checkList, !filtersOpen && styles.checkListHidden)}
          >
            {FILTERS.map((filter) => {
              const checked = activeFilters.includes(filter.id);
              const inputId = `events-filter-${filter.id}`;
              return (
                <label key={filter.id} className={styles.check} htmlFor={inputId}>
                  <span className={cx(styles.box, checked && styles.boxChecked)} aria-hidden>
                    {checked && <span className={styles.tick} />}
                  </span>
                  <input
                    id={inputId}
                    type="checkbox"
                    className={styles.nativeCheck}
                    checked={checked}
                    onChange={() => toggleFilter(filter.id)}
                  />
                  <span className={styles.checkLabel}>{_t(filter.label)}</span>
                </label>
              );
            })}
          </div>
          <button
            type="button"
            className={cx(styles.filterBtn, filtersOpen && styles.filterBtnOpen)}
            aria-expanded={filtersOpen}
            aria-controls="events-filters"
            onClick={() => setFiltersOpen((open) => !open)}
          >
            <img src={iconFilter} alt="" width={13} height={12} />
            <span>{_t('Настроить фильтр')}</span>
          </button>
        </div>
      </div>

      <Preloader isLoading={status === RequestStatus.REQUEST}>
        {cards.length ? (
          <ul className={styles.grid}>
            {cards.map((card) => {
              const to = `${targetDetailsPath}/${card.slug || card.id}`;
              return (
                <li
                  key={card.id}
                  className={cx(styles.card, card.featured && styles.cardFeatured)}
                  style={card.featured ? {
                    ['--event-hero' as string]: `url(${eventHero})`,
                  } : undefined}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.tag}>{card.tag}</span>
                  </div>
                  <div className={styles.cardBody}>
                    {card.canLink ? (
                      <Link to={to} className={styles.cardTitle}>{card.title}</Link>
                    ) : (
                      <span className={styles.cardTitle}>{card.title}</span>
                    )}
                    <p className={styles.cardMeta}>{card.meta}</p>
                    {card.canRegister ? (
                      <ButtonV4
                        type="button"
                        label="Регистрация"
                        variant="outline"
                        tone={card.featured ? 'frosted' : 'onLight'}
                        iconSrc={iconPlus}
                        onClick={() => openApplicationForm({
                          type: 'event',
                          id: card.id,
                          title: card.title,
                          path: `${targetDetailsPath}/${card.slug || card.id}`,
                        })}
                      />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <NoData count={0} />
        )}

        {totalPages > 1 ? (
          <nav className={styles.pager} aria-label={_t('Страницы')}>
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                type="button"
                className={cx(styles.pageBtn, safePage === index && styles.pageBtnActive)}
                onClick={() => setPage(index)}
              >
                {index + 1}
              </button>
            ))}
          </nav>
        ) : null}
      </Preloader>

      {!embedded ? (
        <section className={styles.seeAlso}>
          <h2 className={styles.seeTitle}>{_t('Смотрите также')}</h2>
          <div className={styles.seeGrid}>
            {seeAlsoItems.map((item) => (
              <div
                key={item.id}
                className={styles.seeCard}
                style={{ backgroundImage: `url(${item.image})` }}
              >
                <div className={styles.seeCardTitle}>{_t(item.title)}</div>
                <ButtonV4
                  to={item.to}
                  label="Перейти в раздел"
                  variant="solid"
                  tone="onLight"
                  iconSrc={iconArrow}
                />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default EventsList;
